import type { ApolloQueryResult } from 'apollo-client';

import type { DwellTimeHelperData } from '../analytics/DwellTimeHelper';

import type {
	ContentState,
	ContentMode,
	ContentType,
	ConsumptionCompletedEvent,
	ConsumptionInitiatedEvent,
	ConsumptionViewedEvent,
	ConsumptionErrorEvent,
	ConsumptionEventRequiredData,
	ConsumptionInitiatedEventData,
	ConsumptionContentAttributes,
} from './ConsumptionEvents';
import type { getConsumptionEventDataQuery } from './__types__/getConsumptionEventDataQuery.js';

type unknownString = string | null | undefined;

export const mapTypeToContentState = (
	status: unknownString,
	contentState?: ContentState,
): ContentState => {
	if (contentState) {
		return contentState;
	}
	switch (status) {
		case 'current':
			return 'published';
		case 'archived':
			return 'archived';
		case 'draft':
			return 'draft';
		default:
			return 'unset';
	}
};

export const mapSubTypeToContentMode = (
	subType: unknownString,
	contentMode?: ContentMode,
): ContentMode => {
	if (contentMode) {
		return contentMode;
	}

	switch (subType) {
		case 'live':
			return 'live';
		case 'embed':
			return 'embedded';
		default:
			return 'classic';
	}
};

export const mapTypeToContentType = (
	type: unknownString,
	contentType?: ContentType,
): ContentType => {
	if (contentType) {
		return contentType;
	}

	switch (type) {
		case 'page':
			return 'page';
		case 'blogpost':
			return 'blogpost';
		case 'whiteboard':
			return 'whiteboard';
		case 'database':
			return 'database';
		case 'embed':
			return 'smartlink';
		default:
			return 'unset';
	}
};

export const createConsumptionViewedEvent = (
	response: ApolloQueryResult<getConsumptionEventDataQuery>,
	data: ConsumptionEventRequiredData,
	contentAttributesOverride?: Partial<ConsumptionContentAttributes>,
): ConsumptionViewedEvent => {
	const contentType = mapTypeToContentType(
		response?.data?.singleContent?.type,
		contentAttributesOverride?.contentType,
	);
	const contentState = mapTypeToContentState(
		response?.data?.singleContent?.status,
		contentAttributesOverride?.contentState,
	);
	const contentMode = mapSubTypeToContentMode(
		response?.data?.singleContent?.subType,
		contentAttributesOverride?.contentMode,
	);

	return {
		type: 'sendTrackEvent',
		data: {
			action: 'viewed',
			actionSubject: 'consumption',
			actionSubjectId: 'client',
			source: data.source,
			attributes: {
				contentType,
				contentMode,
				contentState,
				spaceId: response?.data.space?.id ?? 'unset',
				contentId: response?.data.singleContent?.id ?? 'unset',
			},
		},
	};
};

export const createConsumptionInitiatedEvent = (
	response: ApolloQueryResult<getConsumptionEventDataQuery>,
	data: ConsumptionInitiatedEventData,
	contentAttributesOverride?: Partial<ConsumptionContentAttributes>,
): ConsumptionInitiatedEvent => {
	const contentType = mapTypeToContentType(
		response?.data?.singleContent?.type,
		contentAttributesOverride?.contentType,
	);
	const contentState = mapTypeToContentState(
		response?.data?.singleContent?.status,
		contentAttributesOverride?.contentState,
	);
	const contentMode = mapSubTypeToContentMode(
		response?.data?.singleContent?.subType,
		contentAttributesOverride?.contentMode,
	);

	return {
		type: 'sendTrackEvent',
		data: {
			action: 'initiated',
			actionSubject: 'consumption',
			actionSubjectId: 'client',
			source: data.source,
			attributes: {
				contentType,
				contentMode,
				contentState,
				spaceId: response?.data.space?.id ?? 'unset',
				contentId: response?.data.singleContent?.id ?? 'unset',
				utmSource: data.utmSource,
			},
		},
	};
};

export const createConsumptionCompletedEvent = (
	event: ConsumptionViewedEvent,
	dwellTimeHelperData: DwellTimeHelperData,
	visibilityState: DocumentVisibilityState,
): ConsumptionCompletedEvent => {
	return {
		type: 'sendTrackEvent',
		data: {
			...event.data,
			action: 'completed',
			attributes: {
				...event.data.attributes,
				dwellTime: dwellTimeHelperData.attributes.dwellTime,
				pageVisibility: visibilityState,
			},
		},
	};
};

export const createConsumptionErrorEvent = (
	errorMessage: string,
	props: ConsumptionEventRequiredData,
): ConsumptionErrorEvent => {
	return {
		type: 'sendTrackEvent',
		data: {
			action: 'error',
			actionSubject: 'consumption',
			actionSubjectId: 'client',
			source: props.source,
			attributes: {
				error: errorMessage,
				contentId: props.contentId ?? 'unset',
				spaceKey: props.spaceKey,
			},
		},
	};
};
